import {
    FC,
    FormEvent,
    ReactElement,
    useRef,
    useState,
} from 'react'

import { PlannedUserComments } from '../../../../components/PlannedUserCard/subcomponents'
import { CommentViewModel } from '../../../../models'
import { FormActionButtons, Textarea, TimeInput } from '../../../compositions'
import { Comment } from '../../../entities/Comment/Comment'
import { transformDateAndTimesToPeriod } from '../../../entities/Period/PeriodTransformers'
import { EditShiftPlanningFormData, ShiftPlanning } from '../../../entities/ShiftPlanning/ShiftPlanning'
import { formatDate } from '../../../helpers/date'
import useCheckPermission from '../../../helpers/hooks/useCheckPermission'
import trans from '../../../helpers/trans'
import { isValidTimeString } from '../../../helpers/validation/validation'
import { DateFormat } from '../../../types/dateFormatTypes'

import './EditShiftPlanningForm.scss'

interface EditShiftPlanningFormProps {
    isLoading: boolean;
    shiftPlanning: ShiftPlanning;
    onCancel: () => void;
    onSubmit: (formData: EditShiftPlanningFormData) => void;
    onDeleteComment?: (comment: CommentViewModel | Comment) => void;
    className?: string;
}

const EditShiftPlanningForm: FC<EditShiftPlanningFormProps> = ({
    isLoading,
    shiftPlanning,
    onCancel,
    onSubmit,
    onDeleteComment,
    className = '',
}): ReactElement => {
    const formRef = useRef<HTMLFormElement>(null)

    const [comments, setComments] = useState<Comment[]>(shiftPlanning.comments)
    const canEditComments = useCheckPermission('edit-all-comments')

    const [start, setStart] = useState<string>(formatDate(shiftPlanning.period.start, DateFormat.hoursMinutes))
    const [end, setEnd] = useState<string>(formatDate(shiftPlanning.period.end, DateFormat.hoursMinutes))
    const [comment, setComment] = useState<string>('')

    const isValid = isValidTimeString(start) && isValidTimeString(end)

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const period = transformDateAndTimesToPeriod(shiftPlanning.period.start, start, end)

        onSubmit({
            period,
            comment: comment || undefined,
        })
    }

    const handleDeleteComment = (commentToDelete: CommentViewModel | Comment) => {
        setComments(comments.filter(c => c.id !== commentToDelete.id))

        onDeleteComment?.(commentToDelete)
    }

    return (
        <form ref={formRef} onSubmit={handleSubmit} className={`edit-shift-planning-form ${className}`}>
            <div className="edit-shift-planning-form__row">
                <TimeInput
                    label={trans('common.startTime')}
                    value={start}
                    onChange={setStart}
                    className="edit-shift-planning-form__col"
                />

                <TimeInput
                    label={trans('common.endTime')}
                    value={end}
                    onChange={setEnd}
                    className="edit-shift-planning-form__col"
                />
            </div>

            {!! comments?.length && (
                <div className="edit-shift-planning-form__row">
                    <div className="edit-shift-planning-form__col">
                        <div className="input-label">{trans('containers.forms.editShiftPlanningForm.currentComments')}</div>
                        <PlannedUserComments
                            comments={comments}
                            isEditable={canEditComments}
                            onDeleteComment={handleDeleteComment}
                            className="edit-shift-planning-form__comments-list"
                        />
                    </div>
                </div>
            )}

            <div className="edit-shift-planning-form__row">
                <Textarea
                    label={trans('common.addComment')}
                    rows={4}
                    value={comment}
                    onChange={setComment}
                    className="edit-shift-planning-form__col"
                />
            </div>

            <FormActionButtons
                isLoading={isLoading}
                disabled={! isValid}
                submitText={trans('common.modify')}
                onCancelClick={onCancel}
            />
        </form>
    )
}

export default EditShiftPlanningForm
